import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Check,
  Clock,
  Crown,
  HardHat,
  MapPin,
  Receipt,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  Megaphone,
  ArrowRight,
  FileSignature,
  AlarmClock,
  MessageCircle,
  Map as MapIcon,
  DatabaseBackup,
  Globe,
  Building2,
  Package,
  Star,
} from "lucide-react";
import logo from "@/assets/logo.webp";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
const SystemTourVideo = lazy(() =>
  import("@/components/SystemTourVideo").then((m) => ({ default: m.SystemTourVideo })),
);
const TestimonialsSection = lazy(() =>
  import("@/components/landing/TestimonialsSection").then((m) => ({
    default: m.TestimonialsSection,
  })),
);
import { tArray } from "@/lib/i18n-format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/")({
  component: Landing,
  head: () => ({
    meta: [
      { title: "Business Dite Ads — Construction Workforce & Profit SaaS" },
      {
        name: "description",
        content:
          "All-in-one SaaS for construction: GPS time tracking, AI estimates, contracts, immutable invoicing, payroll and profit per project.",
      },
      { property: "og:title", content: "Business Dite Ads — Construction Workforce & Profit SaaS" },
      { property: "og:url", content: "https://busditeads.com/" },
      {
        property: "og:description",
        content: "Workforce, projects, finances and profit — one platform built for construction.",
      },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/19fb8bd0-fa73-499c-9b80-5713a65eb4cd",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/19fb8bd0-fa73-499c-9b80-5713a65eb4cd",
      },
    ],
    links: [
      { rel: "canonical", href: "https://busditeads.com/" },
      // Preload LCP image so the browser discovers it from the initial HTML
      // (improves Largest Contentful Paint discovery time).
      {
        rel: "preload",
        as: "image",
        href: logo,
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Business Dite Ads",
          url: "https://busditeads.com/",
          logo: "https://busditeads.com/favicon.png",
          description:
            "Construction SaaS for workforce, projects, finances and profit — GPS time tracking, AI estimates, contracts, invoicing and payroll.",
          sameAs: ["https://diteads.com/"],
          contactPoint: {
            "@type": "ContactPoint",
            email: "contact@diteads.com",
            contactType: "customer support",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Business Dite Ads",
          url: "https://busditeads.com/",
        }),
      },
    ],
  }),
});

type FeatureDef = {
  key: string;
  icon: typeof MapPin;
  accent: string;
  glow: string;
  badge?: string;
  highlight?: boolean;
};

const featureDefs: FeatureDef[] = [
  {
    key: "gps",
    icon: MapPin,
    accent: "from-blue-500 to-cyan-400",
    glow: "shadow-[0_0_40px_-10px_rgba(59,130,246,0.6)]",
    badge: "landing.badges.popular",
  },
  {
    key: "ai",
    icon: Sparkles,
    accent: "from-fuchsia-500 to-purple-500",
    glow: "shadow-[0_0_40px_-10px_rgba(217,70,239,0.6)]",
    badge: "landing.badges.ai",
    highlight: true,
  },
  {
    key: "project",
    icon: HardHat,
    accent: "from-amber-500 to-orange-500",
    glow: "shadow-[0_0_40px_-10px_rgba(245,158,11,0.6)]",
  },
  {
    key: "invoice",
    icon: Receipt,
    accent: "from-emerald-500 to-teal-400",
    glow: "shadow-[0_0_40px_-10px_rgba(16,185,129,0.6)]",
  },
  {
    key: "payroll",
    icon: Users,
    accent: "from-rose-500 to-pink-500",
    glow: "shadow-[0_0_40px_-10px_rgba(244,63,94,0.6)]",
  },
  {
    key: "profit",
    icon: TrendingUp,
    accent: "from-green-500 to-emerald-400",
    glow: "shadow-[0_0_40px_-10px_rgba(34,197,94,0.6)]",
    badge: "landing.badges.profit",
  },
  {
    key: "extras",
    icon: Clock,
    accent: "from-indigo-500 to-blue-500",
    glow: "shadow-[0_0_40px_-10px_rgba(99,102,241,0.6)]",
  },
  {
    key: "audit",
    icon: ShieldCheck,
    accent: "from-slate-600 to-slate-400",
    glow: "shadow-[0_0_40px_-10px_rgba(100,116,139,0.6)]",
  },
  {
    key: "changeOrders",
    icon: FileSignature,
    accent: "from-violet-500 to-indigo-500",
    glow: "shadow-[0_0_40px_-10px_rgba(139,92,246,0.6)]",
  },
  {
    key: "autoClockOut",
    icon: AlarmClock,
    accent: "from-red-500 to-orange-500",
    glow: "shadow-[0_0_40px_-10px_rgba(239,68,68,0.6)]",
  },
  {
    key: "whatsapp",
    icon: MessageCircle,
    accent: "from-green-500 to-emerald-500",
    glow: "shadow-[0_0_40px_-10px_rgba(34,197,94,0.6)]",
  },
  {
    key: "liveMap",
    icon: MapIcon,
    accent: "from-sky-500 to-blue-500",
    glow: "shadow-[0_0_40px_-10px_rgba(14,165,233,0.6)]",
  },
  {
    key: "backup",
    icon: DatabaseBackup,
    accent: "from-cyan-500 to-teal-400",
    glow: "shadow-[0_0_40px_-10px_rgba(6,182,212,0.6)]",
    badge: "landing.badges.new",
    highlight: true,
  },
  {
    key: "clientPortal",
    icon: Globe,
    accent: "from-blue-500 to-indigo-500",
    glow: "shadow-[0_0_40px_-10px_rgba(59,130,246,0.6)]",
    badge: "landing.badges.new",
  },
  {
    key: "team",
    icon: Building2,
    accent: "from-purple-500 to-fuchsia-500",
    glow: "shadow-[0_0_40px_-10px_rgba(168,85,247,0.6)]",
  },
  {
    key: "materials",
    icon: Package,
    accent: "from-amber-500 to-yellow-400",
    glow: "shadow-[0_0_40px_-10px_rgba(245,158,11,0.6)]",
  },
];

/** Reveals children with a soft fade/rise the first time they enter the viewport. */
function Reveal({
  children,
  className,
  delay = 0,
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: "div" | "section";
}) {
  const ref = useRef<HTMLElement | null>(null);
  // Visible by default so SSR / no-JS / crawlers always see the content.
  const [shown, setShown] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const rect = el.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    if (inView) return; // already on screen — keep it visible, no flash
    setShown(false); // off-screen: arm the reveal
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
      className={cn(
        "transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform motion-reduce:transition-none",
        shown ? "translate-y-0 opacity-100 blur-0" : "translate-y-8 opacity-0 blur-[6px]",
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/** Floating blurred colour orbs + dotted grid — the premium ambient layer. */
function AmbientOrbs({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
    >
      <div className="premium-grid absolute inset-0" />
      <div className="animate-float-orb absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[oklch(0.62_0.19_255)] opacity-25 blur-3xl sm:h-96 sm:w-96" />
      <div className="animate-float-orb-alt absolute -right-16 top-10 h-64 w-64 rounded-full bg-[oklch(0.7_0.16_320)] opacity-20 blur-3xl sm:h-80 sm:w-80" />
      <div className="animate-float-orb absolute bottom-0 left-1/3 h-56 w-56 rounded-full bg-[oklch(0.8_0.16_72)] opacity-20 blur-3xl sm:h-72 sm:w-72" />
    </div>
  );
}

function Landing() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const heroStats = [
    { value: "50+", labelKey: "landing.testimonials.stats.companies" },
    { value: "4.9★", labelKey: "landing.testimonials.stats.rating" },
    { value: "98%", labelKey: "landing.testimonials.stats.retention" },
    { value: "3", labelKey: "landing.testimonials.stats.countries" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Business Dite Ads" className="h-9 w-9 object-contain" />
            <span className="text-base font-semibold tracking-tight">
              Business <span className="text-primary">Dite Ads</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to="/pricing">{mounted ? t("pricing.navLink") : "Pricing"}</Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to="/auth">{mounted ? t("landing.signIn") : "Sign in"}</Link>
            </Button>
            <Button
              asChild
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Link to="/auth">{mounted ? t("landing.startFree") : "Start free"}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(60% 50% at 50% 0%, color-mix(in oklab, var(--primary) 25%, transparent), transparent 70%)",
            }}
          />
          <AmbientOrbs />
          <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
            <div className="mx-auto max-w-3xl text-center">
              <div className="relative mx-auto mb-6 h-28 w-28">
                <div
                  aria-hidden
                  className="absolute inset-0 -z-10 rounded-full opacity-70 blur-2xl"
                  style={{ background: "var(--gradient-brand)" }}
                />
                <img
                  src={logo}
                  alt="Business Dite Ads logo"
                  width={196}
                  height={196}
                  fetchPriority="high"
                  decoding="sync"
                  className="h-28 w-28 object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.18)]"
                />
              </div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-card/70 px-3.5 py-1.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                </span>
                {t("landing.badge")}
              </div>
              <h1
                className="text-balance bg-clip-text text-4xl font-bold leading-[1.05] tracking-tight text-transparent sm:text-6xl"
                style={{ backgroundImage: "var(--gradient-brand)" }}
              >
                {t("landing.heroTitle")}
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-pretty text-base text-muted-foreground sm:text-lg">
                {t("landing.heroDesc")}
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  className="premium-sheen relative w-full overflow-hidden bg-primary text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5 hover:bg-primary/90 sm:w-auto"
                >
                  <Link to="/auth">
                    {mounted ? t("landing.startTrial") : "Start 14-day free trial"}
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="w-full border-primary/40 bg-card/40 text-primary backdrop-blur transition-colors hover:bg-primary/10 sm:w-auto"
                >
                  <Link to="/auth">{mounted ? t("landing.signIn") : "Sign in"}</Link>
                </Button>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                {mounted
                  ? t("landing.noCard")
                  : "Card required · Cancel anytime · No charge for 14 days"}
              </p>

              {/* Trust strip */}
              <div className="mx-auto mt-12 grid max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/40 shadow-[var(--shadow-soft)] backdrop-blur sm:grid-cols-4">
                {heroStats.map((s) => (
                  <div key={s.labelKey} className="bg-card/70 px-4 py-4">
                    <div className="flex items-center justify-center gap-1 text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                      {s.value.endsWith("★") ? (
                        <>
                          {s.value.replace("★", "")}
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        </>
                      ) : (
                        s.value
                      )}
                    </div>
                    <div className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t(s.labelKey)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* System tour video (localized per user language) */}
        <Suspense fallback={null}>
          <SystemTourVideo />
        </Suspense>

        {/* Features */}
        <section className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 opacity-50"
            style={{
              background:
                "radial-gradient(60% 60% at 50% 0%, color-mix(in oklab, var(--primary) 18%, transparent), transparent 70%)",
            }}
          />
          <Reveal className="mb-12 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {t("landing.featuresSubtitle")}
            </div>
            <h2
              className="text-balance bg-clip-text text-3xl font-bold leading-tight tracking-tight text-transparent sm:text-5xl"
              style={{ backgroundImage: "var(--gradient-brand)" }}
            >
              {t("landing.featuresTitle")}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featureDefs.map((f, i) => (
              <Reveal key={f.key} delay={(i % 4) * 80} className="h-full">
                <Card
                  className={cn(
                    "premium-sheen group relative flex h-full flex-col overflow-hidden border-border/60 bg-card/70 p-6 backdrop-blur transition-all duration-300",
                    "hover:-translate-y-1.5 hover:border-primary/40",
                    f.glow,
                    "hover:shadow-[0_24px_70px_-15px_color-mix(in_oklab,var(--primary)_45%,transparent)]",
                    f.highlight && "ring-1 ring-primary/40 lg:scale-[1.02]",
                  )}
                >
                  {/* Animated gradient bar on top */}
                  <div
                    className={cn(
                      "absolute inset-x-0 top-0 h-1 bg-gradient-to-r opacity-80 transition-opacity group-hover:opacity-100",
                      f.accent,
                    )}
                  />
                  {/* Hover glow background */}
                  <div
                    aria-hidden
                    className={cn(
                      "pointer-events-none absolute -inset-px -z-10 bg-gradient-to-br opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20",
                      f.accent,
                    )}
                  />

                  {f.badge && (
                    <span
                      className={cn(
                        "absolute right-3 top-3 rounded-full bg-gradient-to-r px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg",
                        f.accent,
                      )}
                    >
                      {t(f.badge)}
                    </span>
                  )}

                  <div
                    className={cn(
                      "mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3",
                      f.accent,
                    )}
                  >
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">
                    {t(`landing.features.${f.key}.title`)}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {t(`landing.features.${f.key}.desc`)}
                  </p>

                  <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                    <span>{mounted ? t("landing.startFree") : "Start free"}</span>
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <Suspense fallback={null}>
          <TestimonialsSection />
        </Suspense>

        {/* Pricing */}
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
          <Reveal className="mb-8 text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {t("pricing.landingTitle")}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">{t("pricing.landingSubtitle")}</p>
          </Reveal>
          <div className="mx-auto grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { key: "starter", price: "$29", highlight: false, custom: false },
              { key: "pro", price: "$59", highlight: true, custom: false },
              { key: "business", price: "$159", highlight: false, custom: false },
              { key: "enterprise", price: "$350", highlight: false, custom: false },
            ].map((plan) => {
              const features = tArray(t, `pricing.plans.${plan.key}.features`);
              return (
                <Card
                  key={plan.key}
                  className={`relative flex flex-col overflow-hidden p-6 ${
                    plan.highlight ? "border-primary/40 shadow-[var(--shadow-glow)]" : ""
                  }`}
                >
                  {plan.highlight && (
                    <div className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                      {t("pricing.mostPopular")}
                    </div>
                  )}
                  {plan.custom && (
                    <div className="absolute right-3 top-3 rounded-full bg-amber-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      <Crown className="inline h-3 w-3 -mt-0.5 mr-0.5" />
                      {t("pricing.enterprise")}
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {t(`pricing.plans.${plan.key}.name`)}
                    </p>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span
                        className={`font-bold tracking-tight ${plan.custom ? "text-2xl" : "text-4xl"}`}
                      >
                        {plan.price}
                      </span>
                      {!plan.custom && (
                        <span className="text-sm text-muted-foreground">/{t("pricing.month")}</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t(`pricing.plans.${plan.key}.tagline`)}
                    </p>
                  </div>
                  <ul className="mt-5 flex-1 space-y-2">
                    {features.slice(0, 5).map((label) => (
                      <li key={label} className="flex items-start gap-2 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.custom ? (
                    <Button asChild size="lg" variant="outline" className="mt-6 w-full">
                      <a href="mailto:contact@diteads.com?subject=Enterprise%20Plan%20Inquiry">
                        {t("pricing.contactSales")}
                      </a>
                    </Button>
                  ) : (
                    <Button
                      asChild
                      size="lg"
                      variant={plan.highlight ? "default" : "outline"}
                      className={`mt-6 w-full ${plan.highlight ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                    >
                      <Link to="/auth">{t("landing.startFree")}</Link>
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <Button asChild variant="ghost" size="sm">
              <Link to="/pricing">{t("pricing.seeFull")} →</Link>
            </Button>
          </div>
        </section>

        {/* Diteads partner promo */}
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
          <a
            href="https://diteads.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block overflow-hidden rounded-2xl border border-border p-6 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-glow)] sm:p-8"
            style={{ background: "var(--gradient-brand)" }}
          >
            <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-2">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-primary-foreground">
                  <Megaphone className="h-6 w-6" />
                </div>
                <div className="text-primary-foreground">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-primary-foreground/80">
                    {mounted ? t("landing.diteads.eyebrow") : "Marketing Digital · Parceiro"}
                  </p>
                  <h2 className="mt-1 text-xl font-bold sm:text-2xl">
                    {mounted
                      ? t("landing.diteads.title")
                      : "Faça seu negócio crescer com a Diteads"}
                  </h2>
                  <p className="mt-1.5 max-w-xl text-sm text-primary-foreground/85">
                    {mounted
                      ? t("landing.diteads.desc")
                      : "Consultoria, gestão de redes sociais e campanhas em português, inglês e espanhol para impulsionar sua marca."}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-medium text-primary-foreground transition-colors group-hover:bg-white/25">
                    {mounted ? t("landing.diteads.cta") : "Visitar diteads.com"}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
              <div className="overflow-hidden rounded-xl border border-white/20 shadow-lg">
                <video
                  src="/diteads-social-ad.mp4"
                  poster="/diteads-social-ad-poster.jpg"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="aspect-video w-full object-cover"
                />
              </div>
            </div>
          </a>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
          <Reveal
            as="div"
            className="relative overflow-hidden rounded-3xl border border-white/10 p-8 text-center shadow-[var(--shadow-glow)] sm:p-14"
          >
            <div
              aria-hidden
              className="absolute inset-0"
              style={{ background: "var(--gradient-brand)" }}
            />
            <div
              className="premium-grain absolute inset-0 opacity-[0.15] mix-blend-overlay"
              aria-hidden
            />
            <div
              aria-hidden
              className="animate-float-orb absolute -right-10 -top-16 h-56 w-56 rounded-full bg-white/25 blur-3xl"
            />
            <div
              aria-hidden
              className="animate-float-orb-alt absolute -bottom-20 left-0 h-56 w-56 rounded-full bg-[oklch(0.8_0.16_72)]/40 blur-3xl"
            />
            <div className="relative">
              <h2 className="text-balance text-2xl font-bold text-white sm:text-4xl">
                {t("landing.ctaTitle")}
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-pretty text-sm text-white/90 sm:text-base">
                {t("landing.ctaDesc")}
              </p>
              <div className="mt-7">
                <Button
                  asChild
                  size="lg"
                  className="premium-sheen relative overflow-hidden bg-accent text-accent-foreground shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-accent/90"
                >
                  <Link to="/auth">{t("landing.ctaButton")}</Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-border/60 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 text-xs text-muted-foreground sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <Link
              to="/auth"
              aria-label={t("landing.ownerAccess")}
              title={t("landing.ownerTitle")}
              className="text-muted-foreground/30 transition-colors hover:text-amber-500"
            >
              <Crown className="h-3.5 w-3.5" />
            </Link>
            <img src={logo} alt="" className="h-5 w-5 object-contain" />
            <span>© {new Date().getFullYear()} Business Dite Ads</span>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/pricing" className="hover:text-foreground">
              {t("pricing.navLink")}
            </Link>
            <Link to="/privacy" className="hover:text-foreground">
              {t("landing.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              {t("landing.terms")}
            </Link>
            <Link to="/refund" className="hover:text-foreground">
              {t("legal.refund.short")}
            </Link>
            <a href="mailto:contact@diteads.com" className="hover:text-foreground">
              {t("landing.contact")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
